import {
  Box,
  Button,
  Flex,
  PopoverHeader,
  Select,
  Text
} from '@chakra-ui/react';
import React from 'react';

import type { DictionaryOption } from '../../interface/api';
import { INK } from '../../styles/tokens';

const INTER_STACK =
  'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

export type PatientTypeEditStatus =
  | 'loading'
  | 'ready'
  | 'unavailable'
  | 'saving';

export type PatientTypeEditFormProps = {
  readonly currentValue: string;
  readonly options: readonly DictionaryOption[];
  readonly status: PatientTypeEditStatus;
  readonly errorMessage?: string;
  readonly canSave: boolean;
  readonly selectRef: React.RefObject<HTMLSelectElement>;
  readonly onSave: (value: string) => void;
  readonly onCancel: () => void;
};

const WRITE_UNAVAILABLE = "Saving isn't available for this case.";

export const PatientTypeEditForm: React.FC<PatientTypeEditFormProps> = ({
  currentValue,
  options,
  status,
  errorMessage,
  canSave,
  selectRef,
  onSave,
  onCancel
}) => {
  const [selection, setSelection] = React.useState(currentValue);

  const isSaving = status === 'saving';
  const notice = errorMessage ?? (canSave ? undefined : WRITE_UNAVAILABLE);
  // The dictionary is a snapshot; a case can legitimately hold a value that is
  // no longer listed, and dropping it would open the editor on nothing selected.
  const currentIsListed = options.some(
    (option) => option.value === currentValue
  );

  return (
    <Box data-testid="patient-type-edit-form">
      <PopoverHeader
        border="none"
        padding="0 0 8px"
        fontFamily={INTER_STACK}
        fontSize="12.5px"
        fontWeight={700}
        color={INK}
      >
        Edit patient type
      </PopoverHeader>
      <Select
        ref={selectRef}
        data-testid="patient-type-edit-select"
        aria-label="Patient type"
        size="sm"
        fontFamily={INTER_STACK}
        fontSize="12px"
        value={selection}
        isDisabled={status !== 'ready'}
        onChange={(event) => setSelection(event.target.value)}
      >
        {!currentIsListed && (
          <option value={currentValue}>{currentValue || 'N/A'}</option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
      {notice && (
        <Text
          as="p"
          role="alert"
          data-testid="patient-type-edit-error"
          fontFamily={INTER_STACK}
          fontSize="11px"
          fontWeight={500}
          color="red.600"
          marginTop="6px"
        >
          {notice}
        </Text>
      )}
      <Flex justifyContent="flex-end" gap="6px" marginTop="10px">
        <Button
          data-testid="patient-type-edit-cancel"
          size="xs"
          variant="ghost"
          fontFamily={INTER_STACK}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          data-testid="patient-type-edit-save"
          size="xs"
          colorScheme="blue"
          fontFamily={INTER_STACK}
          isLoading={isSaving}
          isDisabled={
            !canSave || status !== 'ready' || selection === currentValue
          }
          onClick={() => onSave(selection)}
        >
          Save
        </Button>
      </Flex>
    </Box>
  );
};
